/**
 * Group descriptions come straight out of MP's WYSIWYG editor. Most are plain
 * text with line breaks, a few carry HTML (paragraphs, bold, spans with inline
 * colours). Render the markup, but only the harmless subset: no attributes
 * survive except a safe href, so inline styles (the old green highlight) and
 * anything script-like are dropped.
 */

const ALLOWED = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 'span']);
const SAFE_HREF = /^(https?:|mailto:|tel:|\/)/i;

export function looksLikeHtml(text: string): boolean {
  return /<[a-z][^>]*>/i.test(text);
}

function clean(node: Node, out: Node, doc: Document): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(doc.createTextNode(child.textContent ?? ''));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style') continue;
    if (!ALLOWED.has(tag)) {
      // Unknown wrapper (div, font, table cell...): keep its text, lose the tag.
      clean(el, out, doc);
      continue;
    }
    const next = doc.createElement(tag);
    if (tag === 'a') {
      const href = el.getAttribute('href') ?? '';
      if (SAFE_HREF.test(href)) {
        next.setAttribute('href', href);
        next.setAttribute('target', '_blank');
        next.setAttribute('rel', 'noopener noreferrer');
      }
    }
    clean(el, next, doc);
    out.appendChild(next);
  }
}

/** Markup safe to hand to dangerouslySetInnerHTML. Plain text passes through untouched. */
export function sanitizeHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return toPlainText(html);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out = doc.createElement('div');
  clean(doc.body, out, doc);
  return out.innerHTML;
}

/** The description with tags removed, for search and the card's short summary. */
export function toPlainText(html: string): string {
  if (!looksLikeHtml(html)) return html;
  return html
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
